import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Badge, CollapsibleSection, colors } from './SharedComponents';
import { API_BASE } from '../config';
import { headlineLabel, FAMILY_LABELS, FAMILY_BADGE_VARIANTS } from '../utils/opportunityDisplay';

/**
 * OpportunityCallout — Lane 51 dossier callout.
 *
 * Mirrors ONE material's persisted Opportunities findings
 * (GET /api/opportunities/material/{materialNumber}) directly on the material
 * dossier, so a buyer sees the opportunity without visiting the separate list.
 * MaterialDossier mounts this in the Order-Optimization slot ONLY when the
 * module is enabled (the flag-ON branch of the swap).
 *
 * HONESTY RULES (design §3, Lane-48 never-additive):
 *  - ACTIVE findings show the dollar FAMILY-LABELED via the shared
 *    `headlineLabel` — volume = "$X/yr opportunity" (prospective), dispersion/
 *    same-day = "$Y overpaid" (observed). The F1/F2 label is NEVER re-implemented
 *    here; it comes from utils/opportunityDisplay so a dossier number can never
 *    read differently from the list page.
 *  - MONITOR / VERIFY findings show NO dollar — only a claim-free tag + the
 *    engine one-liner (the endpoint's row shape carries no dollar field).
 *  - NEVER a combined/summed total across findings; each row shows its own
 *    dollar, the header shows a COUNT, never a sum.
 *  - Renders nothing when the material has no findings (quiet empty state).
 *
 * OFF-lock: reads `opportunitiesEnabled` and bails (no fetch, renders null) when
 * the module is off — defensive belt-and-suspenders on top of the parent's mount
 * gate, and what makes the flag-off "no fetch" invariant unit-testable.
 */
function OpportunityCallout({ materialNumber }) {
  const navigate = useNavigate();
  const { token, opportunitiesEnabled } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    // OFF-lock + guards: no flag, no token, no material -> never fetch.
    if (!opportunitiesEnabled || !token || !materialNumber) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/opportunities/material/${encodeURIComponent(materialNumber)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return; // 404 (flag flipped off mid-session) -> render nothing
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        // Network error -> render nothing; the rest of the dossier stays usable.
      }
    })();
    return () => { cancelled = true; };
  }, [materialNumber, token, opportunitiesEnabled]);

  if (!opportunitiesEnabled || !data) return null;

  const active = data.active || [];
  const monitor = data.monitor || [];
  const verify = data.verify || [];
  const total = active.length + monitor.length + verify.length;
  if (total === 0) return null; // quiet empty state = render nothing

  return (
    <CollapsibleSection title="Opportunities" indicator={`(${total} flagged)`} defaultOpen>
      <div data-testid="opportunity-callout" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {active.map((f) => (
          <FindingRow
            key={f.finding_id}
            testId="opp-callout-active"
            onClick={() => navigate(`/opportunities/${f.finding_id}`)}
          >
            <div style={rowHeadStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Badge variant={FAMILY_BADGE_VARIANTS[f.family_type] || 'primary'} size="small">
                  {FAMILY_LABELS[f.family_type] || f.family_type}
                </Badge>
                <span style={supplierStyle}>{f.supplier_display}</span>
              </div>
              {/* Family-labeled dollar — the ONLY place a dollar renders. */}
              <span data-testid="opp-callout-headline" style={dollarStyle}>
                {headlineLabel(f.family_type, f.headline_value)}
              </span>
            </div>
            {f.ai_question ? <div style={oneLinerStyle}>{f.ai_question}</div> : null}
          </FindingRow>
        ))}

        {/* Lane 355 (O-2): CLASS_MONITOR carries TWO flavors since Lane 352.
            The "At best price" tag is the suppression story and is FALSE for a
            price-gone row (the best price is no longer obtainable), so the tag
            branches on the server-echoed discriminator. The engine one-liner
            below it is already flavor-correct (engine-authored, verbatim).
            Pre-Lane-352 rows carry no price_gone key -> today's tag exactly. */}
        {monitor.map((f) => (
          <ClassifiedRow
            key={f.finding_id}
            testId="opp-callout-monitor"
            tagLabel={f.price_gone ? 'Best price gone' : 'At best price'}
            tone={f.price_gone ? 'primary' : 'success'}
            supplier={f.supplier_display}
            oneLiner={f.ai_question}
            onClick={() => navigate(`/opportunities/${f.finding_id}`)}
          />
        ))}

        {verify.map((f) => (
          <ClassifiedRow
            key={f.finding_id}
            testId="opp-callout-verify"
            tagLabel="Verify data"
            tone="warning"
            supplier={f.supplier_display}
            oneLiner={f.ai_question}
            onClick={() => navigate(`/opportunities/${f.finding_id}`)}
          />
        ))}
      </div>
    </CollapsibleSection>
  );
}

// A clickable finding row shell (matches the dossier's rounded-card row style).
function FindingRow({ children, onClick, testId }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      style={{
        padding: '12px 16px',
        borderRadius: '8px',
        border: `1px solid ${colors.mediumGray}`,
        background: colors.white,
        cursor: 'pointer',
      }}
    >
      {children}
    </div>
  );
}

// A claim-free monitor/verify row — tag + supplier + one-liner, NO dollar.
function ClassifiedRow({ tagLabel, tone, supplier, oneLiner, onClick, testId }) {
  return (
    <FindingRow testId={testId} onClick={onClick}>
      <div style={rowHeadStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Badge variant={tone} size="small">{tagLabel}</Badge>
          <span style={supplierStyle}>{supplier}</span>
        </div>
      </div>
      {oneLiner ? <div style={oneLinerStyle}>{oneLiner}</div> : null}
    </FindingRow>
  );
}

const rowHeadStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: '12px',
};

const supplierStyle = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: colors.darkText,
};

const dollarStyle = {
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'monospace',
  fontWeight: 'var(--font-weight-semibold)',
  color: colors.success,
  whiteSpace: 'nowrap',
};

const oneLinerStyle = {
  marginTop: '6px',
  fontSize: 'var(--font-size-xs)',
  color: colors.secondaryText,
  lineHeight: 1.4,
};

export default OpportunityCallout;
